import { HttpErrorResponse, HttpRequest, HttpHandlerFn, HttpEvent } from '@angular/common/http';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, switchMap, shareReplay, finalize, map, tap } from 'rxjs/operators';
import { UserApiService } from '../services/user-api.service';
import { AuthRefreshResponse } from '../schemas/auth.dto';

/** Default function to determine if a URL is a public auth endpoint */
function defaultIsPublicAuthEndpoint(url: string): boolean {
  const u = url.toLowerCase();

  // Logout requires a bearer token
  if (u.includes('/auth/logout')) return false;

  // All other /auth/ endpoints are public (login, refresh, validate, etc.)
  return u.includes('/auth/');
}

export interface AuthTokenHandlerConfig {
  /** Custom headers to add to all requests (e.g., { 'x-admin-portal': '1' }) */
  customHeaders?: Record<string, string>;
  /** Function to determine if a URL is a public auth endpoint (default: checks for /auth/*) */
  isPublicAuthEndpoint?: (url: string) => boolean;
  /** Header name for retry marker (default: 'x-refresh-retry') */
  retryHeader?: string;
}

/**
 * Shared authentication token handler for HTTP interceptors.
 * Manages token attachment, refresh logic, and retry on 401 errors.
 *
 * Token storage is handled in-memory via UserService (from @vtnz/ui).
 * Refresh token is sent via HTTP-only cookie.
 */
export class AuthTokenHandler {
  private readonly RETRY_HEADER: string;
  private readonly customHeaders: Record<string, string>;
  private readonly isPublicAuthEndpoint: (url: string) => boolean;

  // Shared refresh observable to coalesce concurrent 401s
  private refreshToken$: Observable<string> | null = null;

  constructor(config: AuthTokenHandlerConfig = {}) {
    this.RETRY_HEADER = config.retryHeader || 'x-refresh-retry';
    this.customHeaders = config.customHeaders || {};
    this.isPublicAuthEndpoint = config.isPublicAuthEndpoint || defaultIsPublicAuthEndpoint;
  }

  /**
   * Handle the HTTP request with authentication logic.
   *
   * @param req - The HTTP request
   * @param next - The next handler in the chain
   * @param userApiService - The user API service for token refresh
   * @param getToken - Function to get the current access token from memory
   * @param setToken - Function to set the access token in memory after refresh
   * @param getUserId - Function to get the current user ID
   * @param onLogout - Function to call when logout is needed
   */
  handle(
    req: HttpRequest<unknown>,
    next: HttpHandlerFn,
    userApiService: UserApiService,
    getToken: () => string | null,
    setToken: (token: string) => void,
    getUserId: () => string | null,
    onLogout: () => void
  ): Observable<HttpEvent<unknown>> {
    // Skip adding Authorization for public endpoints to avoid loops
    const isPublic = this.isPublicAuthEndpoint(req.url);

    const token = getToken();
    const initialReq = !isPublic && token ? this.cloneRequest(req, token) : this.cloneRequest(req);

    return next(initialReq).pipe(
      catchError((err: unknown) => {
        const is401 = err instanceof HttpErrorResponse && err.status === 401;

        // Don't try to refresh for public endpoints, or if already retried
        const alreadyRetried = initialReq.headers.has(this.RETRY_HEADER);

        if (!is401 || isPublic || alreadyRetried) {
          return throwError(() => err);
        }

        // Lazily create a single shared refresh observable
        if (!this.refreshToken$) {
          this.refreshToken$ = this.requestTokenRefresh(
            userApiService,
            getUserId,
            setToken,
            onLogout
          ).pipe(
            // Always reset the shared observable once it completes/errors
            finalize(() => {
              this.refreshToken$ = null;
            })
          );
        }

        // Wait for refresh, then retry the original request with the new token
        return this.refreshToken$.pipe(
          catchError(() => {
            // Refresh request itself failed (e.g., 401 from refresh endpoint)
            console.debug('Token refresh failed, logging out');
            onLogout();
            return EMPTY;
          }),
          switchMap((newAccessToken) => {
            console.debug('Retrying request with new token');
            const retriedReq = this.cloneRequest(
              req.clone({
                setHeaders: { [this.RETRY_HEADER]: '1' }, // mark as retried
              }),
              newAccessToken
            );
            return next(retriedReq);
          })
        );
      })
    );
  }

  private cloneRequest(req: HttpRequest<unknown>, token?: string): HttpRequest<unknown> {
    const headers: Record<string, string> = {};

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Add all custom headers
    Object.assign(headers, this.customHeaders);

    return req.clone({
      setHeaders: headers,
      withCredentials: true,
    });
  }

  private requestTokenRefresh(
    userApiService: UserApiService,
    getUserId: () => string | null,
    setToken: (token: string) => void,
    onLogout: () => void
  ): Observable<string> {
    const userId = getUserId();

    if (!userId) {
      console.debug('No userId, logging out');
      onLogout();
      return EMPTY;
    }

    // Refresh token is sent via cookie (withCredentials: true)
    console.debug('Refreshing token');
    return userApiService.refreshToken({ userId }).pipe(
      tap((res: AuthRefreshResponse) => {
        setToken(res.apiToken);
        console.debug('Refreshed token successfully');
      }),
      map((res: AuthRefreshResponse) => res.apiToken),
      shareReplay({ bufferSize: 1, refCount: false })
    );
  }
}
